/**
 * Claims Management API Client
 * Handles all API calls for claims, notifications, and claim rules
 */

import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Get auth token from localStorage
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

// ============================================================================
// Claims CRUD
// ============================================================================

export const createClaim = async (claimData) => {
  const response = await axios.post(
    `${API_URL}/api/v1/claims`,
    claimData,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const listClaims = async (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.status) params.append('status', filters.status);
  if (filters.oem) params.append('oem', filters.oem);
  if (filters.part_number) params.append('part_number', filters.part_number);
  if (filters.claim_type) params.append('claim_type', filters.claim_type);
  if (filters.from_date) params.append('from_date', filters.from_date);
  if (filters.to_date) params.append('to_date', filters.to_date);
  if (filters.assigned_to) params.append('assigned_to', filters.assigned_to);
  if (filters.limit) params.append('limit', filters.limit);
  if (filters.offset) params.append('offset', filters.offset);
  
  const response = await axios.get(
    `${API_URL}/api/v1/claims?${params.toString()}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const getClaimDetail = async (claimId) => {
  const response = await axios.get(
    `${API_URL}/api/v1/claims/${claimId}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const updateClaim = async (claimId, claimData) => {
  const response = await axios.put(
    `${API_URL}/api/v1/claims/${claimId}`,
    claimData,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const updateClaimStatus = async (claimId, statusData) => {
  const response = await axios.patch(
    `${API_URL}/api/v1/claims/${claimId}/status`,
    statusData,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const settleClaim = async (claimId, settlementData) => {
  const response = await axios.post(
    `${API_URL}/api/v1/claims/${claimId}/settle`,
    settlementData,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const deleteClaim = async (claimId) => {
  const response = await axios.delete(
    `${API_URL}/api/v1/claims/${claimId}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// ============================================================================
// Watchers
// ============================================================================

export const addWatcher = async (claimId, userId) => {
  const response = await axios.post(
    `${API_URL}/api/v1/claims/${claimId}/watchers`,
    { user_id: userId },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const removeWatcher = async (claimId, userId) => {
  const response = await axios.delete(
    `${API_URL}/api/v1/claims/${claimId}/watchers/${userId}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const getWatchers = async (claimId) => {
  const response = await axios.get(
    `${API_URL}/api/v1/claims/${claimId}/watchers`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// ============================================================================
// Notifications
// ============================================================================

export const getNotifications = async (unreadOnly = false, limit = 50, offset = 0) => {
  const params = new URLSearchParams();
  if (unreadOnly) params.append('unread_only', 'true');
  params.append('limit', limit);
  params.append('offset', offset);
  
  const response = await axios.get(
    `${API_URL}/api/v1/notifications?${params.toString()}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const getUnreadCount = async () => {
  const response = await axios.get(
    `${API_URL}/api/v1/notifications/unread-count`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const markNotificationRead = async (notificationId) => {
  const response = await axios.patch(
    `${API_URL}/api/v1/notifications/${notificationId}/read`,
    {},
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const markAllNotificationsRead = async () => {
  const response = await axios.patch(
    `${API_URL}/api/v1/notifications/read-all`,
    {},
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const updateNotificationPreferences = async (preferences) => {
  const response = await axios.put(
    `${API_URL}/api/v1/users/notification-preferences`,
    preferences,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// ============================================================================
// Dashboard & Analytics
// ============================================================================

export const getDashboardMetrics = async () => {
  const response = await axios.get(
    `${API_URL}/api/v1/claims/dashboard/metrics`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const getDashboardTrends = async (months = 12) => {
  const response = await axios.get(
    `${API_URL}/api/v1/claims/dashboard/trends?months=${months}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const getClaimsByOpportunity = async (opportunityId) => {
  const response = await axios.get(
    `${API_URL}/api/v1/claims/by-opportunity/${opportunityId}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const getClaimsByPart = async (partNumber) => {
  const response = await axios.get(
    `${API_URL}/api/v1/claims/by-part/${partNumber}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// ============================================================================
// Auto-Detection
// ============================================================================

export const autoDetectClaims = async (year = null, dryRun = false) => {
  const params = new URLSearchParams();
  if (year) params.append('year', year);
  params.append('dry_run', dryRun);
  
  const response = await axios.post(
    `${API_URL}/api/v1/claims/auto-detect?${params.toString()}`,
    {},
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// ============================================================================
// Claim Rules
// ============================================================================

export const getClaimRules = async () => {
  const response = await axios.get(
    `${API_URL}/api/v1/claim-rules`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const getClaimRule = async (ruleId) => {
  const response = await axios.get(
    `${API_URL}/api/v1/claim-rules/${ruleId}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const updateClaimRule = async (ruleId, ruleData) => {
  const response = await axios.put(
    `${API_URL}/api/v1/claim-rules/${ruleId}`,
    ruleData,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// ============================================================================
// Utility Functions
// ============================================================================

// Re-export from shared formatters for backward compatibility
export { formatCurrencyFromCents as formatCurrency } from '../utils/formatters';

export const getStatusColor = (status) => {
  const colors = {
    draft: 'gray',
    submitted: 'blue',
    under_review: 'yellow',
    approved: 'green',
    in_negotiation: 'purple',
    settled: 'green',
    rejected: 'red',
    closed: 'gray'
  };
  return colors[status] || 'gray';
};

export const getStatusLabel = (status) => {
  const labels = {
    draft: 'Draft',
    submitted: 'Submitted',
    under_review: 'Under Review',
    approved: 'Approved',
    in_negotiation: 'In Negotiation',
    settled: 'Settled',
    rejected: 'Rejected',
    closed: 'Closed'
  };
  return labels[status] || status;
};

export const getSeverityColor = (severity) => {
  const colors = {
    low: 'green',
    medium: 'yellow',
    high: 'orange',
    critical: 'red'
  };
  return colors[severity] || 'gray';
};

export const getClaimTypeLabel = (type) => {
  const labels = {
    volume_shortage: 'Volume Shortage',
    price_deviation: 'Price Deviation',
    material_cost: 'Material Cost',
    exchange_rate: 'Exchange Rate',
    other: 'Other'
  };
  return labels[type] || type;
};


// ============================================================================
// Phase 1 Enhancements API
// ============================================================================

/**
 * Get volume tracking chart data for a claim
 */
export const getClaimVolumes = async (claimId) => {
  const response = await axios.get(
    `${API_URL}/api/v1/claims/${claimId}/volumes`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

/**
 * Get financial metrics for a claim
 */
export const getClaimFinancialMetrics = async (claimId) => {
  const response = await axios.get(
    `${API_URL}/api/v1/claims/${claimId}/financial-metrics`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

/**
 * Filter claims by probability
 */
export const filterClaimsByProbability = async (probability) => {
  const response = await axios.get(
    `${API_URL}/api/v1/claims/filter/by-probability?probability=${probability}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

/**
 * Filter claims by age bucket
 */
export const filterClaimsByAge = async (ageBucket) => {
  const response = await axios.get(
    `${API_URL}/api/v1/claims/filter/by-age?age_bucket=${ageBucket}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

/**
 * Filter claims by workflow stage
 */
export const filterClaimsByStage = async (stage) => {
  const response = await axios.get(
    `${API_URL}/api/v1/claims/filter/by-stage?stage=${stage}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

/**
 * Update Phase 1 fields (probability, financial metrics, dates)
 */
export const updateClaimPhase1Fields = async (claimId, updateData) => {
  const response = await axios.patch(
    `${API_URL}/api/v1/claims/${claimId}/update-phase1-fields`,
    updateData,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

/**
 * Get Phase 1 enhanced dashboard metrics
 */
export const getPhase1DashboardMetrics = async () => {
  const response = await axios.get(
    `${API_URL}/api/v1/claims/dashboard/phase1-metrics`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

/**
 * Get workflow stage display name
 */
export const getStageDisplayName = (stage) => {
  const stageNames = {
    claim_definition: 'Claim Definition',
    team_definition: 'Team Definition',
    customer_contact: 'Customer Contact',
    calculation: 'Calculation',
    cost_price_profit: 'Cost | Price | Profit',
    quote_negotiation: 'Quote & Negotiation',
    settlement_invoicing: 'Settlement & Invoicing',
    closed: 'Closed'
  };
  return stageNames[stage] || stage;
};

/**
 * Get probability display info (label, color)
 */
export const getProbabilityInfo = (probability) => {
  const info = {
    low: { label: 'Low', color: 'green', bgColor: 'bg-green-100', textColor: 'text-green-800' },
    medium: { label: 'Medium', color: 'yellow', bgColor: 'bg-yellow-100', textColor: 'text-yellow-800' },
    high: { label: 'High', color: 'red', bgColor: 'bg-red-100', textColor: 'text-red-800' }
  };
  return info[probability] || info.medium;
};



// ============================================================================
// Opportunity Linking
// ============================================================================

export const getAvailableOpportunities = async (search = null) => {
  const params = new URLSearchParams();
  if (search) params.append('search', search);
  
  const response = await axios.get(
    `${API_URL}/api/v1/claims/available-opportunities?${params.toString()}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};


// ============================================================================
// Interactive Workflow - Stage Management
// ============================================================================

export const getStageData = async (claimId, stageName) => {
  const response = await axios.get(
    `${API_URL}/api/v1/claims/${claimId}/stages/${stageName}/data`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const saveStageData = async (claimId, stageName, stageData) => {
  const response = await axios.post(
    `${API_URL}/api/v1/claims/${claimId}/stages/${stageName}/data`,
    { stage_data: stageData },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const completeStage = async (claimId, stageName, notes = null) => {
  const response = await axios.post(
    `${API_URL}/api/v1/claims/${claimId}/stages/${stageName}/complete`,
    { notes },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const reopenStage = async (claimId, stageName, notes = null) => {
  const response = await axios.post(
    `${API_URL}/api/v1/claims/${claimId}/stages/${stageName}/reopen`,
    { notes },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const getAllStagesStatus = async (claimId) => {
  const response = await axios.get(
    `${API_URL}/api/v1/claims/${claimId}/stages/all`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// ============================================================================
// Interactive Workflow - Team Management
// ============================================================================

export const getAvailableUsers = async (claimId) => {
  const response = await axios.get(
    `${API_URL}/api/v1/claims/${claimId}/available-users`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const assignTeamMember = async (claimId, userId, role) => {
  const response = await axios.post(
    `${API_URL}/api/v1/claims/${claimId}/team/assign`,
    { user_id: userId, role },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const getClaimTeam = async (claimId) => {
  const response = await axios.get(
    `${API_URL}/api/v1/claims/${claimId}/team`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const removeTeamMember = async (claimId, assignmentId) => {
  const response = await axios.delete(
    `${API_URL}/api/v1/claims/${claimId}/team/${assignmentId}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// ============================================================================
// Interactive Workflow - Contact Management
// ============================================================================

export const addContact = async (claimId, contactData) => {
  const response = await axios.post(
    `${API_URL}/api/v1/claims/${claimId}/contacts`,
    contactData,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const getContacts = async (claimId) => {
  const response = await axios.get(
    `${API_URL}/api/v1/claims/${claimId}/contacts`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const updateContact = async (claimId, contactId, contactData) => {
  const response = await axios.put(
    `${API_URL}/api/v1/claims/${claimId}/contacts/${contactId}`,
    contactData,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const deleteContact = async (claimId, contactId) => {
  const response = await axios.delete(
    `${API_URL}/api/v1/claims/${claimId}/contacts/${contactId}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// ============================================================================
// Interactive Workflow - Document Management
// ============================================================================

export const uploadDocument = async (claimId, file, stageName, documentType = 'evidence', description = null) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('stage_name', stageName);
  formData.append('document_type', documentType);
  if (description) formData.append('description', description);
  
  const token = localStorage.getItem('token');
  const response = await axios.post(
    `${API_URL}/api/v1/claims/${claimId}/documents/upload`,
    formData,
    { 
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      }
    }
  );
  return response.data;
};

export const linkOpportunityDocument = async (claimId, opportunityDocumentId, stageName, documentType = 'evidence') => {
  const formData = new FormData();
  formData.append('opportunity_document_id', opportunityDocumentId);
  formData.append('stage_name', stageName);
  formData.append('document_type', documentType);
  
  const token = localStorage.getItem('token');
  const response = await axios.post(
    `${API_URL}/api/v1/claims/${claimId}/documents/link-opportunity`,
    formData,
    { 
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      }
    }
  );
  return response.data;
};

export const getClaimDocuments = async (claimId, stageName = null) => {
  const params = stageName ? `?stage_name=${stageName}` : '';
  const response = await axios.get(
    `${API_URL}/api/v1/claims/${claimId}/documents${params}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const getOpportunityDocuments = async (claimId) => {
  const response = await axios.get(
    `${API_URL}/api/v1/claims/${claimId}/opportunity-documents`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const deleteDocument = async (claimId, documentId) => {
  const response = await axios.delete(
    `${API_URL}/api/v1/claims/${claimId}/documents/${documentId}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const getDocumentStats = async (claimId) => {
  const response = await axios.get(
    `${API_URL}/api/v1/claims/${claimId}/documents/stats`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

